import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Filter } from '../../@types/ElasticSearchResponse';
import type QueryFilter from '../../helpers/query-filter';
import type { CombinedFacet } from '../../utils/reselectors';
import AdvancedFilterHelp from './advanced-filter-help';
import SelectFilterItem from './select-filter-item';
import SelectFilterItemAdvanced from './select-filter-item-advanced';

interface SelectFilterProps {
  queryFilter: QueryFilter;
  facet?: CombinedFacet;
  isExcludedFacet?: boolean;
  onRequestSearch: (queryFilter: QueryFilter) => void;
}

const SelectFilter = ({
  queryFilter,
  facet,
  isExcludedFacet,
  onRequestSearch,
}: SelectFilterProps) => {
  const { t } = useTranslation(['common', 'search']);
  const [state, setState] = useState({
    query: '',
    forceVisibility: false,
    filterType: isExcludedFacet ? 1 : 0,
  });

  useEffect(() => {
    if (facet?.isExcludeProhibited && state.filterType) {
      setState((s) => ({ ...s, filterType: 0 }));
    }
  }, [facet?.isExcludeProhibited, state.filterType, setState]);

  const onQuery = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState({ ...state, query: e.currentTarget.value });
  };

  const onChange = (filter: Filter) => {
    if (facet) {
      let nextQueryFilter = queryFilter;

      if (nextQueryFilter.contains(facet.id, filter.displayName)) {
        nextQueryFilter = nextQueryFilter.removePagination().remove(facet.id, filter.displayName);
      } else {
        nextQueryFilter = nextQueryFilter.removePagination().add(facet.id, filter.displayName);
      }

      onRequestSearch(nextQueryFilter);
    }
  };

  const onInclude = (filter: Filter) => {
    if (facet) {
      let nextQueryFilter = queryFilter;
      nextQueryFilter = nextQueryFilter.add(facet.id, filter.displayName);
      onRequestSearch(nextQueryFilter);
    }
  };

  const onExclude = (filter: Filter) => {
    if (facet) {
      let nextQueryFilter = queryFilter;
      nextQueryFilter = nextQueryFilter.exclude(facet.id, filter.displayName);
      onRequestSearch(nextQueryFilter);
    }
  };

  const onForceVisibility = () => setState({ ...state, forceVisibility: true });

  const filters = useMemo(() => {
    if (!facet) return undefined;

    const charMap: Record<string, string> = { Š: 'S', š: 's', Ë: 'E', ë: 'e' };
    const normalize = (str: string) =>
      str.replace(/[ŠšËë]/g, (char) => charMap[char] || char).toLowerCase();

    let filtered = facet.filters;

    if (state.query) {
      const query = state.query.toLowerCase();
      filtered = filtered.filter(({ displayName }) => normalize(displayName).includes(query));
    }

    if (!state.forceVisibility && filtered.length > 25) {
      return filtered.slice(0, 20);
    }

    return filtered;
  }, [facet, state.query, state.forceVisibility]);

  if (!facet) return null;

  return (
    <>
      <div className="filter-panel-body-header">
        {!facet.isExcludeProhibited && (
          <ul className="filter-panel-tab-list">
            <li className={`filter-panel-tab-item${!state.filterType ? ' is-active' : ''}`}>
              <button
                type="button"
                className="filter-panel-tab-action"
                onClick={() => setState({ ...state, filterType: 0 })}
                title={t('Show simple filters', { ns: 'search' })}
              >
                {t('Simple', { ns: 'search' })}
              </button>
            </li>
            <li className={`filter-panel-tab-item${state.filterType ? ' is-active' : ''}`}>
              <button
                type="button"
                className="filter-panel-tab-action"
                onClick={() => setState({ ...state, filterType: 1 })}
                title={t('Show advanced filters', { ns: 'search' })}
              >
                {t('Advanced', { ns: 'search' })}
              </button>
            </li>
          </ul>
        )}

        {!!state.filterType && <AdvancedFilterHelp />}
        <div className="filter-panel-body-header-section">
          <div data-wx-inputsearch="thin">
            <div className="input-search-input-section">
              <form action="" className="input-search-input-form">
                <input
                  type="search"
                  id="input-search"
                  className="input-search-input"
                  value={state.query}
                  onChange={onQuery}
                  aria-label={t('Search in list', { ns: 'search' })}
                  placeholder={t('Search in list', { ns: 'search' })}
                />
                <button type="button" className="input-search-clear" style={{ display: 'none' }} />
              </form>
            </div>
            <div className="input-search-action-section">
              <button
                type="button"
                className="input-search-action is-action"
                title={t('Search', { ns: 'common' })}
              >
                <i className="icon-search no-margin" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="filter-panel-body-content has-search">
        <div data-wx-filterchecklist="">
          <ul className="filter-check-list">
            {filters
              ?.filter((x) => !!x.displayName)
              ?.map((filter, index) =>
                !state.filterType ? (
                  <SelectFilterItem
                    key={`${filter.query}-${filter.selected}`}
                    filter={filter}
                    queryFilter={queryFilter}
                    facet={facet}
                    index={index}
                    onChange={onChange}
                  />
                ) : (
                  <SelectFilterItemAdvanced
                    key={`${filter.query}-${filter.selected}`}
                    filter={filter}
                    queryFilter={queryFilter}
                    facet={facet}
                    onInclude={onInclude}
                    onExclude={onExclude}
                  />
                ),
              )}
            {!state.forceVisibility && facet.filters.length > 25 && (
              <li className="filter-check-list-item">
                <button
                  type="button"
                  className="filter-check-list-extend-btn"
                  onClick={onForceVisibility}
                  title={t('Show more', { ns: 'common' })}
                  data-testid="filter-modal-list-show-more-button"
                >
                  {t('Show more', { ns: 'common' })}
                </button>
              </li>
            )}
          </ul>
        </div>
      </div>
    </>
  );
};

export default SelectFilter;
